import { getShouldQualifyOfferHotelCancellation } from 'selectors/hotelCancellationSelectors'
import { hasAirportTransferPackageInclusions } from 'lib/offer/airportTransferPackageGrouping'
import { useCallback, useContext, useMemo, lazy, Suspense } from 'react'
import * as React from 'react'
import moment from 'moment'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, getEffectiveCharterBundleRoute, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import AccommodationSameRoomOptionTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import OfferPageFocusAnchor from 'components/OfferPage/OfferPageFocusAnchor'
import AccommodationSameRoomOptionTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileDetails'
import AccommodationSameRoomOptionTilePricing from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTilePricing'
import AccommodationSameRoomOptionTileCTA from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileCTA'
import AccommodationSameRoomOptionTileHeaderArea from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileHeaderArea'
import FlashHotelOptionPriceDetails from 'components/OfferPage/FlashHotel/FlashHotelOptionPriceDetails'
import FlashHotelInclusionsToggle from 'components/OfferPage/FlashHotel/FlashHotelInclusionsToggle'
import FlashHotelRateInclusionsContent from 'components/OfferPage/FlashHotel/FlashHotelRateInclusionsContent'
import useFlashHotelRateInclusions, { hasPackageInclusionItems } from 'components/OfferPage/FlashHotel/useFlashHotelRateInclusions'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import getLoyaltyUpgradeTargetDisplayName from 'loyalty/lib/benefits/getLoyaltyUpgradeTargetDisplayName'
import useLoyaltyTargetUpgradePackages from 'loyalty/hooks/useLoyaltyTargetUpgradePackages'
import useLoyaltyOfferPageClearTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageClearTargetStayUpgrade'
import OfferPageLoyaltyStayUpgradeCallOutBanner from 'components/OfferPage/Loyalty/OfferPageLoyaltyStayUpgradeCallOutBanner'
import OfferPageTierProgressionCallOutBanner from 'components/OfferPage/Loyalty/OfferPageTierProgressionCallOutBanner'
import { useAppSelector } from 'hooks/reduxHooks'
import BodyText from 'LuxKit/Typography/BodyText'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { tierDarkForegroundColorStyle } from 'styles/utils/tierStyles'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import useUserCanApplyLoyaltyBenefit from 'loyalty/hooks/useUserCanApplyLoyaltyBenefit'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { AccommodationSameRoomOptionTileSubgrid } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileSubgrid'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import SuggestedPackagePromotionLabel from 'components/OfferPage/SuggestedPackagePromotionLabel'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { toBaseFareClass, type FlightsClassTypes } from 'constants/flight'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { useIntl } from 'react-intl'
import useRebrandVersion from 'hooks/useRebrandVersion'
import PackageInclusionsValidityText, { hasStructuredPackageInclusionsValidityText } from 'components/OfferPage/Accommodation/PackageInclusionsValidityText'
import AirportTransferOption from './AirportTransferOption'
import { getInclusionsWithoutPackageValidityText } from 'components/OfferPage/Accommodation/packageInclusionValidityUtils'
import useFlashHotelPackageOptionImpression from './useFlashHotelPackageOptionImpression'
import ModalContext from 'contexts/ModalContext'
import FlashHotelRoomDetailsModal from '../FlashHotelMobileVariant/Modals/FlashHotelRoomDetailsModal'

const FlashHotelOptionCTAs = lazy(() => import('components/OfferPage/FlashHotel/FlashHotelOptionCTAs'))

const InclusionsContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-areas: 'base-package' 'target-upgrade-package';
  grid-template-rows: 1fr;
`

const BasePackageInclusions = styled.div`
  grid-area: base-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    position: absolute;
    inset: 0;
    opacity: 0;
    transform: translateY(${rem(16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const UpgradedInclusionsHeading = styled(BodyText)`
  margin-block-end: ${rem(8)};
  ${tierDarkForegroundColorStyle}
`

const TargetUpgradePackageInclusions = styled.div`
  grid-area: target-upgrade-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(${rem(0)});
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    position: absolute;
    inset: 0;
    opacity: 0;
    transform: translateY(${rem(-16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const SuggestedPackageBannerContainer = styled.div`
  ${mediaQueryUp.tablet} {
    border-inline: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

interface Props {
  showHotelOnly: boolean
  offer: App.HotelOffer
  packageView: App.OfferPackageOptionView
  /** Every package option available for the selected duration, used to resolve an upgraded room. */
  packageOptions: Array<App.OfferPackageOptionView>
  displayPackageView?: App.OfferPackageOptionView
  /**
   * The canonical snapshot target id for this package row (e.g. `package:<id>`), when the
   * assistant focus feature should be able to scroll/highlight this exact row. Registered as a
   * child of the tile itself (`anchorRelation="parent"`, the default) rather than as a preceding
   * sibling, so this stays a single grid/carousel child -- a sibling anchor would double the
   * carousel's own child count and corrupt its scroll-snap/arrow-scroll math.
   */
  focusTargetId?: string
  packagePosition?: number
  checkIn?: string
  duration: number
  rooms: Array<OfferPageRoom>
  fareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  flightOrigin?: App.Airport
  availableForPurchase?: boolean
  roomsBeforeSurcharge?: number
  flights?: boolean
  isInclusionsValidityEnabled: boolean
  inclusionsOpen?: boolean
  onInclusionsToggle?: () => void
  suggestedPackageMessage?: string
  airportTransferPriceDifference?: number
  airportTransferInclusion?: App.PackageInclusion
  airportTransferBasePackageKey?: string
  airportTransferSelected?: boolean
  onAirportTransferToggle?: (packageKey: string, event: React.ChangeEvent<HTMLInputElement>) => void
  /**
   * Custom content to render in the pricing area.
   * If provided, replaces the default FlashHotelOptionPriceDetails.
   */
  children?: React.ReactNode
}

function FlashHotelRateOption(props: Props) {
  const {
    showHotelOnly,
    offer,
    packageView,
    packageOptions,
    displayPackageView = packageView,
    focusTargetId,
    packagePosition,
    checkIn,
    duration,
    rooms,
    fareClass,
    charterBundleRoute,
    flightOrigin,
    availableForPurchase,
    roomsBeforeSurcharge,
    flights,
    isInclusionsValidityEnabled,
    children,
    inclusionsOpen,
    onInclusionsToggle,
    suggestedPackageMessage,
    airportTransferPriceDifference,
    airportTransferInclusion,
    airportTransferBasePackageKey,
    airportTransferSelected,
    onAirportTransferToggle,
  } = props

  const intl = useIntl()
  const isRebrand = useRebrandVersion() === 'rebrand'
  const pkg = displayPackageView.package
  const suggestedPackagePromotionLabel = suggestedPackageMessage && isRebrand
    ? <SuggestedPackagePromotionLabel message={suggestedPackageMessage} />
    : undefined

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])
  const checkOut = checkOutMoment?.format('YYYY-MM-DD')
  const { impressionRef, reportDisplayedPricing } = useFlashHotelPackageOptionImpression({
    offer,
    packageView,
    packageUniqueKey: pkg.uniqueKey,
    checkIn,
    checkOut,
    rooms,
  })
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: duration,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  const { hasFeederFlightPriced } = useOfferPageFlightPrice()
  const {
    inclusions: visibleEffectiveInclusions,
    bonusInclusions: visibleBonusInclusions,
    luxPlusInclusions,
    hasInclusions,
    shouldShowInclusionsValidityText: shouldShowPackageInclusionsValidityText,
    selectedOccupancy,
    showReturnFlightsInclusion,
    showTasteOfGoldBenefit,
  } = useFlashHotelRateInclusions(pkg, {
    offer,
    checkIn: checkInMoment,
    checkOut: checkOutMoment,
    rooms,
    charterBundleRoute,
    flightOrigin,
    flights,
    hasFeederFlightPriced,
    isInclusionsValidityEnabled,
  })

  const {
    selectedTargetUpgradePackage,
    selectedTargetUpgradeType,
  } = useLoyaltyOfferPageTargetStayUpgrade(pkg)

  const appliedUpgradeDisplayName = selectedTargetUpgradeType === 'room' ?
      (selectedTargetUpgradePackage?.roomType?.name ?? selectedTargetUpgradePackage?.name ?? '') :
      getLoyaltyUpgradeTargetDisplayName(selectedTargetUpgradePackage?.name)

  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const showModal = useContext(ModalContext)

  const upgradedRoomType = selectedTargetUpgradeType === 'room' ? selectedTargetUpgradePackage?.roomType : undefined

  // The details modal looks the room up among this duration's packages, so it has nothing to show
  // when the upgraded room has no package for these dates. Hide the link rather than open an empty modal.
  const canViewUpgradedRoomDetails = !!upgradedRoomType &&
    packageOptions.some(option => option.package.roomType?.id === upgradedRoomType.id)

  const onViewUpgradedRoomDetails = useCallback(() => {
    if (!upgradedRoomType) return
    showModal(<FlashHotelRoomDetailsModal room={upgradedRoomType} />)
  }, [showModal, upgradedRoomType])

  const upgradedInclusionsHeading = <UpgradedInclusionsHeading className={`tier-${displayTier}`} variant="medium" weight="bold">
    {intl.formatMessage({
      id: '66RRbkMfbOZmc0UUAruC',
      defaultMessage: 'Your upgraded inclusions',
      description: 'Heading above the inclusions a loyalty member gains from a free package upgrade',
    })}
  </UpgradedInclusionsHeading>

  const targetUpgradePackages = useLoyaltyTargetUpgradePackages(pkg, offer)
  const loyaltyRoomUpgradePkg = targetUpgradePackages?.room
  const loyaltyPackageUpgradePkg = targetUpgradePackages?.package

  const {
    inclusions: loyaltyRoomUpgradeInclusions,
    bonusInclusions: loyaltyRoomUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyRoomUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyRoomUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    inclusions: loyaltyPackageUpgradeInclusions,
    bonusInclusions: loyaltyPackageUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyPackageUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyPackageUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const shouldShowLoyaltyRoomUpgradeInclusionsValidityText = isInclusionsValidityEnabled && !!loyaltyRoomUpgradePkg?.showInclusionsValidityTag && hasStructuredPackageInclusionsValidityText(loyaltyRoomUpgradePkg?.roomPolicyDescription)
  const shouldShowLoyaltyPackageUpgradeInclusionsValidityText = isInclusionsValidityEnabled && !!loyaltyPackageUpgradePkg?.showInclusionsValidityTag && hasStructuredPackageInclusionsValidityText(loyaltyPackageUpgradePkg?.roomPolicyDescription)
  const visibleLoyaltyRoomUpgradeInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyRoomUpgradeInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText)
  }, [loyaltyRoomUpgradeInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText])
  const visibleLoyaltyRoomUpgradeBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyRoomUpgradeBonusInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText)
  }, [loyaltyRoomUpgradeBonusInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText])
  const visibleLoyaltyPackageUpgradeInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyPackageUpgradeInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText)
  }, [loyaltyPackageUpgradeInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText])
  const visibleLoyaltyPackageUpgradeBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyPackageUpgradeBonusInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText)
  }, [loyaltyPackageUpgradeBonusInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText])
  const hasLoyaltyRoomUpgradeInclusions = hasPackageInclusionItems(
    loyaltyRoomUpgradeLuxPlusInclusions,
    visibleLoyaltyRoomUpgradeInclusions,
    visibleLoyaltyRoomUpgradeBonusInclusions,
  )
  const hasLoyaltyPackageUpgradeInclusions = hasPackageInclusionItems(
    loyaltyPackageUpgradeLuxPlusInclusions,
    visibleLoyaltyPackageUpgradeInclusions,
    visibleLoyaltyPackageUpgradeBonusInclusions,
  )
  const showAirportTransferOption = typeof airportTransferPriceDifference === 'number' && !!airportTransferInclusion && !!airportTransferBasePackageKey && !!onAirportTransferToggle
  const showBasePackageInclusions = hasInclusions || shouldShowPackageInclusionsValidityText || showAirportTransferOption

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  // Loyalty upgrades: applying here upgrades every room, which uses one upgrade per room. Only offer
  // it when the member has enough left; they can still upgrade some of the rooms at checkout.
  const isLoyaltyRoomUpgradesAvailable = useUserCanApplyLoyaltyBenefit('hotel_upgrade', rooms.length)

  useLoyaltyOfferPageClearTargetStayUpgrade(packageView, isLoyaltyRoomUpgradesAvailable)

  return (
    <AccommodationSameRoomOptionTileSubgrid>
      {suggestedPackageMessage && !isRebrand && <SuggestedPackageBannerContainer>
        <MessageBanner kind="promotional" icon={<SolidStarIcon />}>{suggestedPackageMessage}</MessageBanner>
      </SuggestedPackageBannerContainer>}
      <AccommodationSameRoomOptionTile
        data-testid="flash-hotel-package-option"
        data-package-id={pkg.id}
        ref={impressionRef}
      >
        {focusTargetId && <OfferPageFocusAnchor targetId={focusTargetId} />}
        {isLoyaltyEnabled && (
          <AccommodationSameRoomOptionTileHeaderArea>
            {isLoyaltyRoomUpgradesAvailable && (
              <OfferPageLoyaltyStayUpgradeCallOutBanner packageView={packageView} />
            )}
            <OfferPageTierProgressionCallOutBanner
              offer={offer}
              packageView={packageView}
              checkIn={checkIn}
              duration={duration}
              hasLoyaltyAccount={hasLoyaltyAccount}
            />
          </AccommodationSameRoomOptionTileHeaderArea>
        )}
        <AccommodationSameRoomOptionTileDetails
          packageNumber={packagePosition}
          optionName={pkg.name}
          promotionLabel={suggestedPackagePromotionLabel}
          upgradeTitleVariant={selectedTargetUpgradeType === 'room' ? 'roomUpgrade' : 'packageUpgrade'}
          selectedLoyaltyTargetUpgrade={selectedTargetUpgradePackage ? { name: appliedUpgradeDisplayName } : undefined}
          onViewLoyaltyUpgradedRoomDetails={canViewUpgradedRoomDetails ? onViewUpgradedRoomDetails : undefined}
        >
          {availableForPurchase && !selectedTargetUpgradePackage && pkg.roomRate && (
            <FlashCancellationPolicies
              checkInDate={checkIn}
              charterDepartDate={charterBundleRoute ? charterDepartDate : undefined}
              propertyTimezoneOffset={offer.property?.timezoneOffset}
              cancellationPolicy={pkg.roomRate.cancellationPolicy}
              showHotelOnly={showHotelOnly || hasAirportTransferPackageInclusions(packageView.package)}
            />
          )}
          {availableForPurchase && selectedTargetUpgradePackage?.roomRate && (
            <FlashCancellationPolicies
              checkInDate={checkIn}
              charterDepartDate={charterBundleRoute ? charterDepartDate : undefined}
              propertyTimezoneOffset={offer.property?.timezoneOffset}
              cancellationPolicy={selectedTargetUpgradePackage.roomRate.cancellationPolicy}
              showHotelOnly={showHotelOnly || hasAirportTransferPackageInclusions(packageView.package)}
            />
          )}
          {showBasePackageInclusions && (
            <InclusionsContainer>
              <BasePackageInclusions className={clsx({ 'show': !selectedTargetUpgradePackage })}>
                {hasInclusions && (
                  <FlashHotelInclusionsToggle
                    packagePosition={packagePosition}
                    open={inclusionsOpen}
                    onToggle={onInclusionsToggle}
                  >
                    <FlashHotelRateInclusionsContent
                      offer={offer}
                      inclusions={visibleEffectiveInclusions}
                      bonusInclusions={visibleBonusInclusions}
                      luxPlusInclusions={luxPlusInclusions}
                      rooms={rooms}
                      showReturnFlightsInclusion={showReturnFlightsInclusion}
                      showTasteOfGoldBenefit={showTasteOfGoldBenefit}
                    />
                  </FlashHotelInclusionsToggle>
                )}
                {shouldShowPackageInclusionsValidityText && (
                  <PackageInclusionsValidityText policyDescription={pkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
                )}
                {showAirportTransferOption && (
                  <AirportTransferOption
                    checked={airportTransferSelected}
                    inclusion={airportTransferInclusion}
                    packageKey={airportTransferBasePackageKey}
                    onChange={onAirportTransferToggle}
                    priceDifference={airportTransferPriceDifference}
                  />
                )}
              </BasePackageInclusions>
              {loyaltyRoomUpgradePkg && (
                <TargetUpgradePackageInclusions className={clsx({ 'show': selectedTargetUpgradeType === 'room' })}>
                  {hasLoyaltyRoomUpgradeInclusions && upgradedInclusionsHeading}
                  {hasLoyaltyRoomUpgradeInclusions && (
                    <FlashHotelInclusionsToggle
                      packagePosition={packagePosition}
                      open={inclusionsOpen}
                      onToggle={onInclusionsToggle}
                    >
                      <FlashHotelRateInclusionsContent
                        offer={offer}
                        inclusions={visibleLoyaltyRoomUpgradeInclusions}
                        bonusInclusions={visibleLoyaltyRoomUpgradeBonusInclusions}
                        luxPlusInclusions={loyaltyRoomUpgradeLuxPlusInclusions}
                        rooms={rooms}
                        showReturnFlightsInclusion={showReturnFlightsInclusion}
                        showTasteOfGoldBenefit={showTasteOfGoldBenefit}
                      />
                    </FlashHotelInclusionsToggle>
                  )}
                  {shouldShowLoyaltyRoomUpgradeInclusionsValidityText && (
                    <PackageInclusionsValidityText policyDescription={loyaltyRoomUpgradePkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
                  )}
                </TargetUpgradePackageInclusions>
              )}
              {loyaltyPackageUpgradePkg && (
                <TargetUpgradePackageInclusions className={clsx({ 'show': selectedTargetUpgradeType === 'package' })}>
                  {hasLoyaltyPackageUpgradeInclusions && upgradedInclusionsHeading}
                  {hasLoyaltyPackageUpgradeInclusions && (
                    <FlashHotelInclusionsToggle
                      packagePosition={packagePosition}
                      open={inclusionsOpen}
                      onToggle={onInclusionsToggle}
                    >
                      <FlashHotelRateInclusionsContent
                        offer={offer}
                        inclusions={visibleLoyaltyPackageUpgradeInclusions}
                        bonusInclusions={visibleLoyaltyPackageUpgradeBonusInclusions}
                        luxPlusInclusions={loyaltyPackageUpgradeLuxPlusInclusions}
                        rooms={rooms}
                        showReturnFlightsInclusion={showReturnFlightsInclusion}
                        showTasteOfGoldBenefit={showTasteOfGoldBenefit}
                      />
                    </FlashHotelInclusionsToggle>
                  )}
                  {shouldShowLoyaltyPackageUpgradeInclusionsValidityText && (
                    <PackageInclusionsValidityText policyDescription={loyaltyPackageUpgradePkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
                  )}
                </TargetUpgradePackageInclusions>
              )}
            </InclusionsContainer>
          )}
        </AccommodationSameRoomOptionTileDetails>

        <AccommodationSameRoomOptionTilePricing>
          {!children && availableForPurchase && <FlashHotelOptionPriceDetails
            packageView={packageView}
            roomsBeforeSurcharge={roomsBeforeSurcharge}
            onDisplayedPricingChange={reportDisplayedPricing}
          />}
        </AccommodationSameRoomOptionTilePricing>

        <AccommodationSameRoomOptionTileCTA>
          {children}
          {!children && availableForPurchase && (
            <Suspense fallback={null}>
              <FlashHotelOptionCTAs packageView={packageView} />
            </Suspense>
          )}
        </AccommodationSameRoomOptionTileCTA>
      </AccommodationSameRoomOptionTile>
    </AccommodationSameRoomOptionTileSubgrid>
  )
}

type MappedProps = Pick<Props,
  | 'showHotelOnly'
  | 'offer'
  | 'packageOptions'
  | 'checkIn'
  | 'duration'
  | 'rooms'
  | 'fareClass'
  | 'charterBundleRoute'
  | 'flightOrigin'
  | 'availableForPurchase'
  | 'flights'
  | 'isInclusionsValidityEnabled'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  showHotelOnly: getShouldQualifyOfferHotelCancellation(state),
  offer: state.offer as App.HotelOffer,
  packageOptions: getDurationPackageOptions(state, appState),
  checkIn: state.checkInDate,
  duration: state.duration,
  rooms: state.rooms,
  fareClass: toBaseFareClass(state.fareClass),
  charterBundleRoute: getEffectiveCharterBundleRoute(state),
  flightOrigin: state.airport,
  availableForPurchase: isAvailableForPurchase(state, appState),
  flights: state.flights,
  isInclusionsValidityEnabled: !!appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croInclusionValidity],
}), FlashHotelRateOption)
